import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Bot,
  Building2,
  Loader2,
  MessageSquare,
  Plus,
  Send,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import AIMarkdown from "@/components/shared/AIMarkdown";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Give me an ATS operational summary for today.",
  "Which open jobs need the most attention?",
  "Summarize our current hiring pipeline risks.",
  "What should leadership review this week?",
];

export default function ChatGPTWorkspace() {
  const { can } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const chatRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/chatgpt/sessions", { params: { limit: 50 } });
      setSessions(data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, []);

  useEffect(() => {
    if (!can("chatgpt:access")) return;
    api.get("/chatgpt/status")
      .then(({ data }) => setStatus(data))
      .catch((error) => toast.error(apiErr(error)));
    loadSessions();
  }, [can, loadSessions]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  if (!can("chatgpt:access")) {
    return <Navigate to="/" replace />;
  }

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  const openSession = async (selectedSessionId) => {
    try {
      const { data } = await api.get(`/chatgpt/sessions/${selectedSessionId}`);
      setSessionId(data.session_id);
      setMessages(data.messages || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const deleteSession = async (selectedSessionId, event) => {
    event.stopPropagation();
    try {
      await api.delete(`/chatgpt/sessions/${selectedSessionId}`);
      if (selectedSessionId === sessionId) startNewChat();
      await loadSessions();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const updateActionPreview = (previewId, update) => {
    setMessages((current) => current.map((message) => {
      if (message.preview?.id !== previewId) return message;
      return {
        ...message,
        preview: { ...message.preview, ...update },
      };
    }));
  };

  const confirmAction = async (preview) => {
    setActionBusyId(preview.id);
    try {
      const { data } = await api.post(`/chatgpt/actions/${preview.id}/confirm`);
      updateActionPreview(preview.id, {
        status: "confirmed",
        result: data.record,
      });
      toast.success("ChatGPT action completed");
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setActionBusyId(null);
    }
  };

  const cancelAction = async (preview) => {
    setActionBusyId(preview.id);
    try {
      await api.post(`/chatgpt/actions/${preview.id}/cancel`);
      updateActionPreview(preview.id, { status: "cancelled" });
      toast.success("Draft cancelled");
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setActionBusyId(null);
    }
  };

  const send = async (providedMessage) => {
    const message = (providedMessage ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((current) => [
      ...current,
      { role: "user", content: message },
      { role: "assistant", content: "" },
    ]);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chatgpt/chat`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message }),
        },
      );
      if (!response.ok || !response.body) {
        throw new Error("ChatGPT could not start this response.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let answer = "";
      let complete = false;
      while (!complete) {
        const chunk = await reader.read();
        complete = chunk.done;
        if (!chunk.value) continue;
        for (const line of decoder.decode(chunk.value).split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const event = JSON.parse(line.slice(6));
          if (event.type === "delta") {
            answer += event.content;
            setMessages((current) => [
              ...current.slice(0, -1),
              { ...current[current.length - 1], role: "assistant", content: answer },
            ]);
          }
          if (event.type === "action_preview") {
            setMessages((current) => [
              ...current.slice(0, -1),
              {
                ...current[current.length - 1],
                role: "assistant",
                content: answer || "A supervised ATS draft is ready for review.",
                kind: "action_preview",
                preview: event.preview,
              },
            ]);
          }
          if (event.type === "done") setSessionId(event.session_id);
          if (event.type === "error") toast.error(event.message);
        }
      }
      await loadSessions();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div
      className="grid min-h-[calc(100vh-140px)] gap-4 lg:grid-cols-[250px_minmax(0,1fr)]"
      data-testid="chatgpt-workspace"
    >
      <Card className="flex max-h-[calc(100vh-140px)] flex-col overflow-hidden p-3">
        <Button
          variant="outline"
          className="mb-3 justify-start gap-2"
          onClick={startNewChat}
          data-testid="chatgpt-new-chat-button"
        >
          <Plus className="h-4 w-4" /> New chat
        </Button>
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          Conversations
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto">
          {sessions.length === 0 ? (
            <div
              className="px-2 py-4 text-xs text-muted-foreground"
              data-testid="chatgpt-empty-sessions"
            >
              Your ChatGPT conversations will appear here.
            </div>
          ) : sessions.map((session) => (
            <div
              key={session.session_id}
              className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent"
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                onClick={() => openSession(session.session_id)}
                data-testid={`chatgpt-session-${session.session_id}`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{session.title}</span>
              </button>
              <button
                type="button"
                className={
                  "invisible rounded p-1 text-muted-foreground hover:bg-destructive/10 " +
                  "hover:text-destructive group-hover:visible"
                }
                onClick={(event) => deleteSession(session.session_id, event)}
                data-testid={`chatgpt-delete-session-${session.session_id}`}
                aria-label={`Delete ${session.title}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-[#255058] text-white">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold">
                Nexreq AI Workspace
              </h1>
              <p
                className="text-xs text-muted-foreground"
                data-testid="chatgpt-model-status"
              >
                {status
                  ? `${status.model} · ${status.access_scope.replace(/_/g, " ")}`
                  : "Loading workspace"}
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-[#255058]" />
            {status?.write_access ? "Supervised ATS write drafts" : "ATS-aware, read-only analysis"}
          </div>
        </div>

        <div ref={chatRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {messages.length === 0 && (
            <div
              className="mx-auto max-w-3xl py-8"
              data-testid="chatgpt-empty-state"
            >
              <div
                className={
                  "mb-3 flex h-11 w-11 items-center justify-center rounded-md " +
                  "bg-[#255058]/10 text-[#255058]"
                }
              >
                <Building2 className="h-5 w-5" />
              </div>
              <h2 className="font-display text-2xl font-semibold">Ask about your ATS</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Analyze pipeline health, jobs, candidate activity, placements, and
                operational risks.
              </p>
              {status?.write_access && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid="chatgpt-write-access-note"
                >
                  Ask Nexreq AI to create or update a core ATS record. Every change stays a draft until confirmed.
                </p>
              )}
              <div className="mt-6 grid gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((suggestion, index) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="outline"
                    className="h-auto justify-start whitespace-normal px-3 py-3 text-left text-sm"
                    onClick={() => send(suggestion)}
                    data-testid={`chatgpt-suggestion-${index}`}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={
                message.role === "user" ? "ml-auto max-w-3xl" : "mr-auto max-w-3xl"
              }
              data-testid={`chatgpt-message-${message.role}-${index}`}
            >
              <div
                className={
                  message.role === "user"
                    ? "rounded-md bg-[#255058] px-4 py-3 text-sm text-white"
                    : "rounded-md bg-muted px-4 py-3"
                }
              >
                {message.role === "user" ? message.content : (
                  <AIMarkdown
                    text={message.content}
                    testId={`chatgpt-markdown-${index}`}
                  />
                )}
                {streaming &&
                  index === messages.length - 1 &&
                  message.role === "assistant" && (
                  <span className="ml-1 inline-block animate-pulse">▌</span>
                )}
              </div>
              {message.kind === "action_preview" && (
                <ActionPreview
                  preview={message.preview}
                  busy={actionBusyId === message.preview?.id}
                  onConfirm={confirmAction}
                  onCancel={cancelAction}
                />
              )}
            </div>
          ))}
          {streaming && messages[messages.length - 1]?.content === "" && (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="chatgpt-typing-indicator"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> ChatGPT is analyzing ATS data
            </div>
          )}
        </div>

        <div className="border-t p-4">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              rows={2}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
              placeholder="Ask Nexreq AI about Nexreq ATS…"
              className="min-h-[48px] resize-none"
              data-testid="chatgpt-message-input"
            />
            <Button
              size="icon"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              data-testid="chatgpt-send-button"
              aria-label="Send message"
            >
              {streaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ActionPreview({ preview, busy, onConfirm, onCancel }) {
  if (!preview) return null;
  const fields = Object.entries(preview.plan?.fields || {});
  const isPending = preview.status === "pending";
  const isNeedsDetails = preview.status === "needs_details";

  return (
    <div
      className="mt-3 border border-[#255058]/30 bg-[#255058]/5 p-4"
      data-testid={`chatgpt-action-preview-${preview.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">Supervised ATS change</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {preview.plan?.summary}
          </p>
        </div>
        <span
          className="border border-[#255058]/20 bg-background px-2 py-1 text-xs font-medium capitalize"
          data-testid={`chatgpt-action-status-${preview.id}`}
        >
          {preview.status.replace(/_/g, " ")}
        </span>
      </div>
      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
        <div>
          <span className="text-muted-foreground">Record</span>
          <div className="font-medium capitalize">
            {preview.plan?.operation} {preview.plan?.resource}
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Fields</span>
          <div className="font-medium">{fields.length} proposed value(s)</div>
        </div>
      </div>
      {fields.length > 0 && (
        <dl className="mt-3 grid gap-x-4 gap-y-1 border-t pt-3 text-xs sm:grid-cols-2">
          {fields.map(([field, value]) => (
            <div key={field} className="flex gap-2">
              <dt className="shrink-0 text-muted-foreground">{field}</dt>
              <dd className="min-w-0 break-words font-medium">{formatPreviewValue(value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {isNeedsDetails && (
        <div
          className="mt-3 border-l-2 border-amber-500 pl-3 text-xs text-amber-800"
          data-testid={`chatgpt-action-missing-fields-${preview.id}`}
        >
          I need: {(preview.missing_fields || []).join(", ")}. Add these details in your next message.
        </div>
      )}
      {preview.status === "confirmed" && (
        <div className="mt-3 text-xs font-medium text-emerald-700" data-testid={`chatgpt-action-confirmed-${preview.id}`}>
          Change completed and recorded in the ATS audit trail.
        </div>
      )}
      {preview.status === "cancelled" && (
        <div className="mt-3 text-xs text-muted-foreground" data-testid={`chatgpt-action-cancelled-${preview.id}`}>
          This draft was cancelled. No ATS record was changed.
        </div>
      )}
      {(isPending || isNeedsDetails) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {isPending && (
            <Button
              size="sm"
              onClick={() => onConfirm(preview)}
              disabled={busy}
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              data-testid={`chatgpt-confirm-action-${preview.id}`}
            >
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Confirm change
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCancel(preview)}
            disabled={busy}
            data-testid={`chatgpt-cancel-action-${preview.id}`}
          >
            Cancel draft
          </Button>
        </div>
      )}
    </div>
  );
}

function formatPreviewValue(value) {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return String(value ?? "—");
}